import {
  DprSectionsWithoutHistory,
  PreviousDpr,
  StatusTrackerHistoryItem,
  TableOfColumnHistoryItem,
} from '@dpr-details/models';
import {
  DprDetails,
  DprStatusTracker,
  DprStatusTrackerDetails,
  DprStatusTrackerPreviousRow,
  DprTableOfColumnDetail,
  DprTableOfColumns,
  DprTableOfColumnsPreviousRow,
} from '@shared/models/dpr';
import {
  StatusTrackerWithData,
  TableOfColumnWithData,
} from '@shared/models/project-setup';
import { numericIdOrNull } from '@shared/utils';
import {
  getRowAnchorValue,
  getStatusTrackerColumnCarryOver,
  isStatusTrackerColumnCarryOverEnabled,
  isTableOfColumnItemCarryOverEnabled,
  normalizeCarryOverValue,
} from './accumulation.utils';
import {
  getCalendarDay,
  getSelfReferencingFormulaColumnIds,
  getStatusTrackerFormulas,
  getTableOfColumnFormulas,
} from './formula.utils';

/**
 * The DPR before the given one, as far as its carry-overs and self-referencing
 * formulas read it, rebuilt from the previous values its sections were sent with.
 * It is dated the day before, so the formula engine takes its cells as earlier
 * ones. Null when no section came with previous values.
 */
export function buildPreviousDpr(
  dprDetails: DprDetails,
  tablesOfColumns: TableOfColumnWithData[]
): PreviousDpr | null {
  const statusTrackerPreviousValues =
    dprDetails?.dprStatusTracker?.previousValues;
  const hasTableOfColumnsPreviousValues = !!dprDetails?.dprTableOfColumns?.some(
    (dprTableOfColumn) => dprTableOfColumn.previousValues != null
  );
  const dayBefore = getDayBefore(dprDetails?.dprDateTime);

  if (
    (statusTrackerPreviousValues == null && !hasTableOfColumnsPreviousValues) ||
    dayBefore === null
  ) {
    return null;
  }

  return {
    dprDateTime: dayBefore,
    dprStatusTracker:
      statusTrackerPreviousValues == null
        ? null
        : {
            dprStatusTrackerDetails: statusTrackerPreviousValues.flatMap(
              (previousRow) =>
                (previousRow.previousValueItems ?? []).map((item) => ({
                  id: null,
                  dprStatusTrackerId: null,
                  statusTrackerId: +previousRow.statusTrackerId,
                  rowId: +previousRow.rowId,
                  columnId: +item.columnId,
                  colorTypeId: null,
                  value: item.value,
                }))
            ),
          },
    dprTableOfColumns: (tablesOfColumns ?? []).map((tableOfColumn) => ({
      tableOfColumnId: +tableOfColumn.id,
      dprTableOfColumnsDetails: buildPreviousTableOfColumnsRows(
        tableOfColumn,
        dprDetails.dprTableOfColumns?.find(
          (dprTableOfColumn) =>
            +dprTableOfColumn.tableOfColumnId === +tableOfColumn.id
        )?.previousValues
      ),
    })),
  };
}

/**
 * The sections that need previous values but came without the field. A DPR read
 * always sends it, as null when there is no previous DPR, so a missing one means
 * it was read from an API that didn't send it yet, or cached before it did. It is
 * not a first DPR.
 */
export function getSectionsWithoutPreviousValues(
  dprDetails: DprDetails,
  statusTrackers: StatusTrackerWithData[],
  tablesOfColumns: TableOfColumnWithData[]
): DprSectionsWithoutHistory {
  return {
    includesStatusTracker:
      !!dprDetails?.dprStatusTracker &&
      dprDetails.dprStatusTracker.previousValues === undefined &&
      (statusTrackers ?? []).some(
        (statusTracker) =>
          getStatusTrackerPreviousValueColumnIds(statusTracker).length > 0
      ),
    tableOfColumnIds: new Set(
      (dprDetails?.dprTableOfColumns ?? [])
        .filter(
          (dprTableOfColumn) =>
            dprTableOfColumn.previousValues === undefined &&
            getTableOfColumnsPreviousValueItemIds(
              tablesOfColumns?.find(
                (tableOfColumn) =>
                  +tableOfColumn.id === +dprTableOfColumn.tableOfColumnId
              )
            ).length > 0
        )
        .map((dprTableOfColumn) => +dprTableOfColumn.tableOfColumnId)
    ),
  };
}

/**
 * One row per anchor option, carrying the anchor cell that carry-overs and the
 * formula engine match rows by.
 */
function buildPreviousTableOfColumnsRows(
  tableOfColumn: TableOfColumnWithData,
  previousValues: DprTableOfColumnsPreviousRow[] | null | undefined
): DprTableOfColumnDetail[] {
  const anchorColumnId = tableOfColumn.anchorColumn?.anchorColumnId;

  if (anchorColumnId == null) {
    return [];
  }

  return (previousValues ?? []).map((previousRow) => {
    const anchorOptionId = +previousRow.anchorOptionId;
    const rowId = `previous_${tableOfColumn.id}_${anchorOptionId}`;

    return {
      id: rowId,
      dprTableOfColumnId: null,
      dprTableOfColumnDetailItems: [
        {
          id: null,
          dprTableOfColumnDetailId: rowId,
          tableOfColumnItemId: +anchorColumnId,
          value: String(anchorOptionId),
        },
        ...(previousRow.previousValueItems ?? []).map((item) => ({
          id: null,
          dprTableOfColumnDetailId: rowId,
          tableOfColumnItemId: +item.tableOfColumnItemId,
          value: item.value,
        })),
      ],
    };
  });
}

export function buildTableOfColumnsFormulaHistory(
  dpr: PreviousDpr
): TableOfColumnHistoryItem[] {
  return (dpr.dprTableOfColumns ?? []).flatMap((dprTableOfColumn) =>
    (dprTableOfColumn.dprTableOfColumnsDetails ?? []).flatMap((detail) =>
      (detail.dprTableOfColumnDetailItems ?? []).map((item) => ({
        tableOfColumnId: +dprTableOfColumn.tableOfColumnId,
        dprTableOfColumnDetailId: detail.id,
        tableOfColumnItemId: item.tableOfColumnItemId,
        value: item.value,
        dprDate: dpr.dprDateTime,
      }))
    )
  );
}

export function buildStatusTrackerFormulaHistory(
  dpr: PreviousDpr
): StatusTrackerHistoryItem[] {
  return (dpr.dprStatusTracker?.dprStatusTrackerDetails ?? []).map(
    (detail) => ({
      statusTrackerId: +detail.statusTrackerId,
      rowId: detail.rowId,
      columnId: detail.columnId,
      value: detail.value,
      dprDate: dpr.dprDateTime,
    })
  );
}

export function getStatusTrackerCellKey(
  statusTrackerId: number | string,
  rowId: number | string,
  columnId: number | string
): string {
  return `${statusTrackerId}_${rowId}_${columnId}`;
}

/**
 * The id a Status Tracker cell is queued under before it is synced. The offline
 * create and the offline save build it the same way.
 */
export function getStatusTrackerCellId(
  dprDetailId: number | string,
  statusTrackerId: number | string,
  rowId: number | string,
  columnId: number | string
): string {
  return `${dprDetailId}_${getStatusTrackerCellKey(
    statusTrackerId,
    rowId,
    columnId
  )}`;
}

/**
 * The section's cells keyed by status tracker, row and column. Working a DPR out
 * writes every derived cell of every row, and scanning all the cells for each write
 * grows with the square of the tracker's size. The first of two stored copies of a
 * cell wins, as it would in a scan.
 */
export function indexStatusTrackerCells(
  dprStatusTracker: Pick<DprStatusTracker, 'dprStatusTrackerDetails'>
): Map<string, DprStatusTrackerDetails> {
  const cells = new Map<string, DprStatusTrackerDetails>();

  dprStatusTracker?.dprStatusTrackerDetails?.forEach((detail) => {
    const key = getStatusTrackerCellKey(
      detail.statusTrackerId,
      detail.rowId,
      detail.columnId
    );

    if (!cells.has(key)) {
      cells.set(key, detail);
    }
  });

  return cells;
}

/** The columns a DPR's previous values hold: every carry-over source and running total. */
export function getStatusTrackerPreviousValueColumnIds(
  statusTracker: StatusTrackerWithData
): number[] {
  const carryOverSourceIds = (statusTracker?.columns ?? [])
    .filter((column) =>
      isStatusTrackerColumnCarryOverEnabled(statusTracker, column)
    )
    .map(
      (column) =>
        +getStatusTrackerColumnCarryOver(statusTracker, column).carryOverItemId
    );

  return [
    ...new Set([
      ...carryOverSourceIds,
      ...getSelfReferencingFormulaColumnIds(
        getStatusTrackerFormulas(statusTracker)
      ),
    ]),
  ];
}

export function getTableOfColumnsPreviousValueItemIds(
  tableOfColumn: TableOfColumnWithData
): number[] {
  if (tableOfColumn?.anchorColumn?.anchorColumnId == null) {
    return [];
  }

  const carryOverSourceIds = (tableOfColumn.tableOfColumnItems ?? [])
    .filter((item) => isTableOfColumnItemCarryOverEnabled(item))
    .map((item) => +item.carryOver.carryOverItemId);

  return [
    ...new Set([
      ...carryOverSourceIds,
      ...getSelfReferencingFormulaColumnIds(
        getTableOfColumnFormulas(tableOfColumn)
      ),
    ]),
  ];
}

/**
 * The previous values for the DPR after the one the section belongs to, built the
 * way the API builds them: for every template row, the value each carry-over source
 * and running total ended with. The section's own carry-overs and formulas must
 * already be worked out.
 */
export function buildStatusTrackerPreviousValues(
  statusTrackers: StatusTrackerWithData[],
  dprStatusTracker: Pick<DprStatusTracker, 'dprStatusTrackerDetails'>
): DprStatusTrackerPreviousRow[] {
  const cells = indexStatusTrackerCells(dprStatusTracker);

  return (statusTrackers ?? []).flatMap((statusTracker) => {
    const columnIds = getStatusTrackerPreviousValueColumnIds(statusTracker);

    return columnIds.length
      ? (statusTracker.rows ?? []).map((row) => ({
          statusTrackerId: +statusTracker.id,
          rowId: +row.id,
          previousValueItems: columnIds.map((columnId) => ({
            columnId,
            value: normalizeCarryOverValue(
              cells.get(
                getStatusTrackerCellKey(+statusTracker.id, +row.id, columnId)
              )?.value
            ),
          })),
        }))
      : [];
  });
}

/**
 * The same for a table of columns: one entry per row that holds an anchor option,
 * keyed by that option. A row with no option can't be matched on the next DPR, and
 * a repeated option keeps the row created first, as the API does.
 */
export function buildTableOfColumnsPreviousValues(
  tableOfColumn: TableOfColumnWithData,
  dprTableOfColumn: Pick<DprTableOfColumns, 'dprTableOfColumnsDetails'>
): DprTableOfColumnsPreviousRow[] {
  const itemIds = getTableOfColumnsPreviousValueItemIds(tableOfColumn);
  const anchorValues = new Set<string>();

  if (!itemIds.length) {
    return [];
  }

  return sortByCreation(
    dprTableOfColumn?.dprTableOfColumnsDetails ?? []
  ).flatMap((detail) => {
    const anchorValue = getRowAnchorValue(
      detail,
      +tableOfColumn.anchorColumn.anchorColumnId
    );

    if (anchorValue == null || anchorValues.has(anchorValue)) {
      return [];
    }

    anchorValues.add(anchorValue);

    return [
      {
        anchorOptionId: +anchorValue,
        previousValueItems: itemIds.map((tableOfColumnItemId) => ({
          tableOfColumnItemId,
          value: normalizeCarryOverValue(
            detail.dprTableOfColumnDetailItems?.find(
              (item) => item.tableOfColumnItemId === tableOfColumnItemId
            )?.value
          ),
        })),
      },
    ];
  });
}

/**
 * Saved rows by id, then the rows added on the device in the order they were added,
 * as the sync saves those after every saved row.
 */
function sortByCreation(
  details: DprTableOfColumnDetail[]
): DprTableOfColumnDetail[] {
  const getCreationRank = (detail: DprTableOfColumnDetail): number =>
    numericIdOrNull(detail.id) ?? Number.MAX_SAFE_INTEGER;

  return [...details].sort((a, b) => getCreationRank(a) - getCreationRank(b));
}

function getDayBefore(value: Date | string): string | null {
  const day = getCalendarDay(value);

  if (day === null) {
    return null;
  }

  const [year, month, date] = day.split('-').map(Number);

  // Built and read back in UTC, so no time zone can move it to another day.
  return new Date(Date.UTC(year, month - 1, date - 1))
    .toISOString()
    .slice(0, 10);
}
